/**
 * AnchorPals API Service
 * Handles all HTTP requests to the backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

/**
 * Get stored auth token
 */
const getToken = () => localStorage.getItem('dockmate_token');

/**
 * Store auth token
 */
const setToken = (token) => localStorage.setItem('dockmate_token', token);

/**
 * Remove auth token
 */
const removeToken = () => localStorage.removeItem('dockmate_token');

/**
 * Make authenticated API request
 */
async function apiRequest(endpoint, options = {}) {
  const token = getToken();

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'An error occurred' } }));
    const message = error.error?.message || error.message || `HTTP error! status: ${response.status}`;
    throw new Error(message);
  }

  // Handle empty responses
  const text = await response.text();
  return text ? JSON.parse(text) : null;
}

// ======================
// Auth API
// ======================

export const authAPI = {
  register: async (userData) => {
    const response = await apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    if (response.data?.access_token) {
      setToken(response.data.access_token);
    }
    return response.data;
  },

  login: async (credentials) => {
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    if (response.data?.access_token) {
      setToken(response.data.access_token);
    }
    return response.data;
  },

  socialLogin: async (provider, token, extraData = {}) => {
    const response = await apiRequest('/auth/social', {
      method: 'POST',
      body: JSON.stringify({ provider, token, ...extraData }),
    });
    if (response.data?.access_token) {
      setToken(response.data.access_token);
    }
    return response.data;
  },

  logout: async () => {
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } finally {
      removeToken();
    }
  },

  getCurrentUser: async () => {
    const response = await apiRequest('/auth/me');
    return { user: response.data };
  },

  getToken,
  removeToken,
};

// ======================
// Users API
// ======================

export const usersAPI = {
  getProfile: async () => {
    return apiRequest('/users/profile');
  },

  updateProfile: async (updates) => {
    return apiRequest('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  getUserById: async (userId) => {
    return apiRequest(`/users/${userId}`);
  },

  getUserRatings: async (userId) => {
    return apiRequest(`/users/${userId}/ratings`);
  },

  getPublicProfile: async (userId) => {
    return apiRequest(`/users/${userId}/public-profile`);
  },

  uploadProfilePhoto: async (file) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('photo', file);

    const response = await fetch(`${API_BASE_URL}/users/profile-photo`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
      throw new Error(error.error?.message || error.message || 'Upload failed');
    }

    const result = await response.json();
    return result.data;
  },

  getMyReviews: async () => {
    return apiRequest('/users/me/reviews');
  },

  getMyStats: async () => {
    return apiRequest('/users/me/stats');
  },

  getMySentReviews: async () => {
    return apiRequest('/users/me/sent-reviews');
  },
};

// ======================
// Yachts API
// ======================

export const yachtsAPI = {
  getMyYachts: async () => {
    return apiRequest('/yachts');
  },

  getYachtById: async (yachtId) => {
    return apiRequest(`/yachts/${yachtId}`);
  },

  createYacht: async (yachtData) => {
    return apiRequest('/yachts', {
      method: 'POST',
      body: JSON.stringify(yachtData),
    });
  },

  updateYacht: async (yachtId, updates) => {
    return apiRequest(`/yachts/${yachtId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  uploadPhoto: async (yachtId, file) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('photo', file);

    const response = await fetch(`${API_BASE_URL}/yachts/${yachtId}/photo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to upload photo');
    }

    return response.json();
  },

  deletePhoto: async (yachtId, photoUrl) => {
    return apiRequest(`/yachts/${yachtId}/photo`, {
      method: 'DELETE',
      body: JSON.stringify({ photo_url: photoUrl }),
    });
  },

};

// ======================
// Helpers API
// ======================

export const helpersAPI = {
  getMyProfile: async () => {
    const response = await apiRequest('/helpers/profile');
    return { profile: response.data };
  },

  updateProfile: async (updates) => {
    const response = await apiRequest('/helpers/profile', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return response.data;
  },

  getHelperById: async (helperId) => {
    const response = await apiRequest(`/helpers/${helperId}`);
    return response.data;
  },

  toggleAvailability: async (available) => {
    const response = await apiRequest('/helpers/available', {
      method: 'PATCH',
      body: JSON.stringify({ available }),
    });
    return response.data;
  },

  getServiceAreas: async () => {
    const response = await apiRequest('/helpers/service-areas');
    return { service_areas: response.data };
  },

  addServiceArea: async (portId, portName) => {
    const response = await apiRequest('/helpers/service-areas', {
      method: 'POST',
      body: JSON.stringify({ port_id: portId, port_name: portName }),
    });
    return response.data;
  },

  removeServiceArea: async (areaId) => {
    return apiRequest(`/helpers/service-areas/${areaId}`, {
      method: 'DELETE',
    });
  },

  searchHelpersAtPort: async (portId) => {
    const response = await apiRequest(`/helpers/at-port/${portId}`);
    return response.data;
  },

  // Get helper's services (for My Services page)
  getMyServices: async (status) => {
    const url = status && status !== 'all'
      ? `/helpers/my-services?status=${status}`
      : '/helpers/my-services';
    return apiRequest(url);
  },

  // Document management
  getDocuments: async () => {
    return apiRequest('/helpers/documents');
  },

  uploadDocument: async (file, documentType, description) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    if (description) {
      formData.append('description', description);
    }

    const response = await fetch(`${API_BASE_URL}/helpers/documents`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
      throw new Error(error.error?.message || error.message || 'Upload failed');
    }

    const result = await response.json();
    return result.data; // Return the document directly
  },

  deleteDocument: async (documentId) => {
    return apiRequest(`/helpers/documents/${documentId}`, {
      method: 'DELETE',
    });
  },

  getHelperDocuments: async (helperId) => {
    return apiRequest(`/helpers/${helperId}/documents`);
  },

  uploadProfilePhoto: async (file) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('photo', file);

    const response = await fetch(`${API_BASE_URL}/helpers/profile-photo`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
      throw new Error(error.error?.message || error.message || 'Upload failed');
    }

    const result = await response.json();
    return result.data;
  },

  hideService: async (requestId) => {
    return apiRequest(`/helpers/services/${requestId}/hide`, {
      method: 'POST',
    });
  },
};

// ======================
// Ports API
// ======================

export const portsAPI = {
  search: async (query) => {
    const response = await apiRequest(`/ports?q=${encodeURIComponent(query)}`);
    return { ports: response.data };
  },

  getById: async (portId) => {
    const response = await apiRequest(`/ports/${portId}`);
    return response.data;
  },

  getByCountry: async (country) => {
    const response = await apiRequest(`/ports/country/${encodeURIComponent(country)}`);
    return response.data;
  },
};

// ======================
// Requests API
// ======================

export const requestsAPI = {
  create: async (requestData) => {
    return apiRequest('/requests', {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
  },

  getMyRequests: async (status) => {
    const params = status ? `?status=${status}` : '';
    return apiRequest(`/requests${params}`);
  },

  getById: async (requestId) => {
    return apiRequest(`/requests/${requestId}`);
  },

  update: async (requestId, updates) => {
    return apiRequest(`/requests/${requestId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  cancel: async (requestId) => {
    return apiRequest(`/requests/${requestId}`, {
      method: 'DELETE',
    });
  },

  cancelRequest: async (requestId) => {
    return apiRequest(`/requests/${requestId}/cancel`, {
      method: 'POST',
    });
  },

  // For helpers - uses same endpoint, backend filters by user type
  getAvailableRequests: async () => {
    return apiRequest('/requests');
  },

  applyToRequest: async (requestId, message) => {
    return apiRequest(`/requests/${requestId}/apply`, {
      method: 'POST',
      body: JSON.stringify({ cover_message: message }),
    });
  },

  getApplications: async (requestId) => {
    return apiRequest(`/requests/${requestId}/applications`);
  },

  selectHelper: async (requestId, helperId) => {
    return apiRequest(`/requests/${requestId}/select`, {
      method: 'POST',
      body: JSON.stringify({ helper_ids: [helperId] }),
    });
  },

  startService: async (requestId) => {
    return apiRequest(`/requests/${requestId}/start`, {
      method: 'PUT',
    });
  },

  completeService: async (requestId) => {
    return apiRequest(`/requests/${requestId}/mark-completed`, {
      method: 'POST',
    });
  },

  markCompleted: async (requestId) => {
    return apiRequest(`/requests/${requestId}/mark-completed`, {
      method: 'POST',
    });
  },

  completeAndRate: async (requestId, rating, comment) => {
    return apiRequest(`/requests/${requestId}/complete-and-rate`, {
      method: 'POST',
      body: JSON.stringify({ rating, comment }),
    });
  },

  withdrawApplication: async (requestId, reason) => {
    return apiRequest(`/requests/${requestId}/withdraw`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    });
  },

  deleteRequest: async (requestId) => {
    return apiRequest(`/requests/${requestId}`, {
      method: 'DELETE',
    });
  },

  dismissRequest: async (requestId) => {
    return apiRequest(`/requests/${requestId}/dismiss`, {
      method: 'POST',
    });
  },

  fulfillRequest: async (requestId) => {
    return apiRequest(`/requests/${requestId}/fulfill`, {
      method: 'POST',
    });
  },
};

// ======================
// Chat API
// ======================

export const chatAPI = {
  getConversations: async () => {
    return apiRequest('/chat/conversations');
  },

  getConversation: async (conversationId) => {
    return apiRequest(`/chat/conversations/${conversationId}`);
  },

  getOrCreateConversation: async (userId) => {
    return apiRequest('/chat/conversations', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  },

  getMessages: async (conversationId, limit = 50, before) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (before) params.append('before', before);
    return apiRequest(`/chat/conversations/${conversationId}/messages?${params}`);
  },

  sendMessage: async (conversationId, content) => {
    return apiRequest(`/chat/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  },

  markAsRead: async (conversationId) => {
    return apiRequest(`/chat/conversations/${conversationId}/read`, {
      method: 'POST',
    });
  },

  // Get or create conversation for a specific request
  getRequestConversation: async (requestId, otherUserId) => {
    const params = otherUserId ? `?user_id=${otherUserId}` : '';
    return apiRequest(`/chat/request/${requestId}${params}`);
  },

  // Get or create GROUP conversation for a request (owner + all helpers)
  getGroupConversation: async (requestId) => {
    return apiRequest(`/chat/request/${requestId}/group`);
  },
};

// ======================
// Ratings API
// ======================

export const ratingsAPI = {
  submit: async (requestId, rating, comment) => {
    return apiRequest(`/requests/${requestId}/rate`, {
      method: 'POST',
      body: JSON.stringify({ rating, comment }),
    });
  },

  submitForHelper: async (requestId, helperId, rating, comment) => {
    return apiRequest(`/requests/${requestId}/rate`, {
      method: 'POST',
      body: JSON.stringify({ rating, comment, helper_id: helperId }),
    });
  },

  getForUser: async (userId) => {
    return apiRequest(`/ratings/user/${userId}`);
  },

  getForService: async (serviceId) => {
    return apiRequest(`/ratings/service/${serviceId}`);
  },

  update: async (ratingId, { rating, comment }) => {
    return apiRequest(`/ratings/${ratingId}`, {
      method: 'PUT',
      body: JSON.stringify({ rating, comment }),
    });
  },

  delete: async (ratingId) => {
    return apiRequest(`/ratings/${ratingId}`, {
      method: 'DELETE',
    });
  },
};

// ======================
// Notifications API
// ======================

export const notificationsAPI = {
  getAll: async (limit = 50, unreadOnly = false) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (unreadOnly) params.append('unread_only', 'true');
    return apiRequest(`/notifications?${params}`);
  },

  markAsRead: async (notificationId) => {
    return apiRequest(`/notifications/${notificationId}/read`, {
      method: 'PATCH',
    });
  },

  markAllAsRead: async () => {
    return apiRequest('/notifications/read-all', {
      method: 'POST',
    });
  },

  delete: async (notificationId) => {
    return apiRequest(`/notifications/${notificationId}`, {
      method: 'DELETE',
    });
  },

  clearAll: async () => {
    return apiRequest('/notifications', {
      method: 'DELETE',
    });
  },
};

// ======================
// Admin API
// ======================

export const adminAPI = {
  getOverviewStats: async () => {
    const response = await apiRequest('/admin/stats');
    return response.data;
  },
  getUserStats: async () => {
    const response = await apiRequest('/admin/stats/users');
    return response.data;
  },
  getRequestStats: async () => {
    const response = await apiRequest('/admin/stats/requests');
    return response.data;
  },
  getHelperStats: async () => {
    const response = await apiRequest('/admin/stats/helpers');
    return response.data;
  },
  getPlatformStats: async () => {
    const response = await apiRequest('/admin/stats/platform');
    return response.data;
  },
  getUsers: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await apiRequest(`/admin/users${queryString ? '?' + queryString : ''}`);
    return response;
  },
  getUserById: async (userId) => {
    const response = await apiRequest(`/admin/users/${userId}`);
    return response.data;
  },
  updateUserStatus: async (userId, status, reason) => {
    const response = await apiRequest(`/admin/users/${userId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reason }),
    });
    return response.data;
  },
  getAllRequests: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await apiRequest(`/admin/requests${queryString ? '?' + queryString : ''}`);
    return response;
  },
  getPendingHelpers: async () => {
    const response = await apiRequest('/admin/helpers/pending');
    return response.data;
  },
  verifyHelper: async (helperId, action, note) => {
    const response = await apiRequest(`/admin/helpers/${helperId}/verify`, {
      method: 'POST',
      body: JSON.stringify({ action, note }),
    });
    return response.data;
  },
  sendBulkMessage: async (messageData) => {
    const response = await apiRequest('/admin/email', {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
    return response.data;
  },
};

export default {
  auth: authAPI,
  users: usersAPI,
  yachts: yachtsAPI,
  helpers: helpersAPI,
  ports: portsAPI,
  requests: requestsAPI,
  chat: chatAPI,
  ratings: ratingsAPI,
  notifications: notificationsAPI,
  admin: adminAPI,
};
